// phase-compiler-tab-pins (Kimi D2) — the Bookmarks & Links candidate filter.
//
// Applicability (= "the navigation editor can inspect this PDF artifact") is NOT
// guessed on the client any more: the previous version mirrored the backend
// `shippingFormatOf` and wrongly treated a CONVERTED Word document as a PDF even in
// a Health-Canada `docx-required` / EMA `pdf-or-docx` slot, where the ORIGINAL Word
// bytes ship and the server refuses navigation. The client cannot know the slot
// (it needs region + CtdSectionCode + the tenant-shadowed SectionFormatRulesCatalog
// + original-retention), so capability is now computed SERVER-SIDE from the one
// canonical packaging authority and delivered as a per-submission map
// (useNavigationCapability / GET …/navigation-capability).
//
// This module is therefore a pure, order-preserving FILTER over that authoritative
// map — consumed by BOTH the first-applicable auto-select AND its Previous/Next so
// the two can never disagree. The per-selected-document envelope's
// `unsupportedReason` remains the final authority.

import type { NavigationCapabilityMap } from '@/components/compiler/inspector/navigation/useNavigationCapability';

/** The minimal document shape the filter reads: just its id. */
export interface NavigationCandidateDoc {
  readonly Id: string;
}

/**
 * Filter an already-ordered document list down to the navigation-review-capable
 * ones per the authoritative server capability map, PRESERVING the caller's order
 * (the canonical nav order from buildDocGroups). When the map has not loaded yet
 * (null/undefined) NOTHING is applicable — the auto-select then waits rather than
 * landing on a document the server would reject.
 */
export function navigationApplicableDocs<T extends NavigationCandidateDoc>(
  ordered: readonly T[],
  capability: NavigationCapabilityMap | null | undefined,
): T[] {
  if (!capability) return [];
  return ordered.filter((d) => capability[d.Id] === true);
}
